import { useCallback, useState } from 'react';
import { z } from 'zod';
import type {
  Classifier,
  CoverageType,
  EmployeeBucket,
  OccupancyFeature,
  Ownership,
  PortfolioScope,
  PropertyType,
  TivBucket,
} from '@zg/shared/property';
import {
  loadClassifier,
  saveClassifier as persistClassifier,
} from '../lib/propertyStorage';

const classifierSchema = z.object({
  propertyType: z.enum(['apartment', 'condo', 'student', 'senior', 'mixed-use']),
  portfolioScope: z.enum(['single', 'multiple']),
  ownership: z.enum(['owner-operator', 'developer-holding', 'association', 'other']),
  state: z.string().length(2, 'Select a state'),
  coverages: z
    .array(z.enum(['property', 'gl', 'umbrella', 'wc']))
    .min(1, 'Pick at least one coverage'),
  features: z.array(
    z.enum([
      'pool',
      'spa',
      'playground',
      'clubhouse',
      'daycare',
      'dog-park',
      'commercial-tenants',
      'subsidized',
    ])
  ),
  employees: z.enum(['none', '1-10', '11-50', '50+']),
  tivBucket: z.enum(['<5M', '5-25M', '25-100M', '100M+']),
});

export type ClassifierDraft = {
  propertyType?: PropertyType;
  portfolioScope?: PortfolioScope;
  ownership?: Ownership;
  state?: string;
  coverages?: CoverageType[];
  features?: OccupancyFeature[];
  employees?: EmployeeBucket;
  tivBucket?: TivBucket;
};

export type ClassifierErrors = Partial<Record<keyof Classifier, string>>;

export interface UsePropertyClassifierResult {
  draft: ClassifierDraft;
  errors: ClassifierErrors;
  update: <K extends keyof Classifier>(key: K, value: Classifier[K]) => void;
  validate: () => { ok: true; classifier: Classifier } | { ok: false; errors: ClassifierErrors };
  reset: () => void;
}

export function usePropertyClassifier(): UsePropertyClassifierResult {
  const [draft, setDraft] = useState<ClassifierDraft>(() => {
    const existing = loadClassifier();
    return existing ?? { coverages: [], features: [] };
  });
  const [errors, setErrors] = useState<ClassifierErrors>({});

  const update = useCallback(<K extends keyof Classifier>(key: K, value: Classifier[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setErrors((e) => {
      if (!(key in e)) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  }, []);

  const validate = useCallback((): ReturnType<
    UsePropertyClassifierResult['validate']
  > => {
    const parsed = classifierSchema.safeParse(draft);
    if (parsed.success) {
      persistClassifier(parsed.data);
      setErrors({});
      return { ok: true, classifier: parsed.data };
    }
    const fieldErrors: ClassifierErrors = {};
    for (const issue of parsed.error.issues) {
      const key = issue.path[0] as keyof Classifier | undefined;
      if (key && !fieldErrors[key]) {
        fieldErrors[key] = issue.message;
      }
    }
    setErrors(fieldErrors);
    return { ok: false, errors: fieldErrors };
  }, [draft]);

  const reset = useCallback(() => {
    setDraft({ coverages: [], features: [] });
    setErrors({});
  }, []);

  return { draft, errors, update, validate, reset };
}
